import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBRL, formatPct, formatDateTime, formatDate } from "@/lib/format";
import { defaultPeriod } from "@/lib/periods";
import { PeriodFilter } from "@/components/PeriodFilter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, FileText, ChevronRight, ChevronDown, Sheet, Snowflake } from "lucide-react";

export default function DRE() {
  const { currentOrg } = useAuth();
  const [period, setPeriod] = useState(defaultPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // {type:'sales'|'expenses', title, rows}
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get(`/finance/summary`, { params: { date_from: period.from, date_to: period.to } });
      setData(s.data);
    } finally { setLoading(false); }
  }, [period.from, period.to]);

  useEffect(() => { load(); }, [load, currentOrg?.id]);

  const openSales = async () => {
    const r = await api.get(`/finance/sales`, { params: { date_from: period.from, date_to: period.to } });
    setDrill({ type: "sales", title: "Vendas do período", rows: r.data });
  };
  const openCmv = () => {
    const rows = (c?.composicao?.cmv || []).slice();
    const total = rows.reduce((a, r) => a + (Number(r.cost_total) || 0), 0);
    setDrill({ type: "cmv", title: "Memória de cálculo do CMV", rows, total });
  };

  const openExpenses = (type) => {
    const comp = (type === "FIXED" ? c?.composicao?.despesas_fixas : c?.composicao?.despesas_variaveis) || [];
    const map = {};
    for (const e of comp) {
      const key = e.name || "Sem categoria";
      if (!map[key]) map[key] = { name: key, amount: 0, count: 0, auto: false, items: [] };
      map[key].amount += Number(e.amount) || 0;
      map[key].count += 1;
      if (e.auto) map[key].auto = true;
      map[key].items.push(e);
    }
    const rows = Object.values(map).sort((a, b) => b.amount - a.amount);
    const total = rows.reduce((a, r) => a + r.amount, 0);
    setExpanded({});
    setDrill({
      type: "expenses",
      title: type === "FIXED" ? "Despesas Fixas do período" : "Despesas Variáveis do período",
      rows,
      total,
    });
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/finance/dre.pdf`, { params: { date_from: period.from, date_to: period.to }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `dre_${period.from}_${period.to}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("DRE em PDF gerada");
    } catch { toast.error("Falha ao gerar PDF"); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get(`/finance/composition.xlsx`, { params: { date_from: period.from, date_to: period.to }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `dre_composicao_${period.from}_${period.to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Composição em Excel gerada");
    } catch { toast.error("Falha ao gerar Excel"); }
  };

  const c = data?.current;
  const cmp = data?.comparison;
  const pctOf = (v) => (c && c.receita_bruta ? formatPct((v / c.receita_bruta) * 100) : "—");

  const Row = ({ label, value, strong, isResult, pctVal, onClick, cmpKey, testid, sign }) => (
    <div
      className={`flex items-center justify-between px-5 py-3 ${strong ? "bg-secondary font-semibold" : ""} ${onClick ? "cursor-pointer hover:bg-secondary/60" : ""} ${isResult ? "border-t-2 border-border" : "border-b border-border"}`}
      onClick={onClick} data-testid={testid}
    >
      <span className={`${strong ? "font-display" : ""} text-sm`}>{sign}{label}{onClick && <span className="text-accent text-xs ml-2">ver detalhes</span>}</span>
      <span className="flex items-center gap-6">
        <span className="font-num text-sm w-28 text-right">{formatBRL(value)}</span>
        <span className="font-num text-xs text-muted-foreground w-16 text-right hidden sm:inline">{pctVal != null ? pctVal : pctOf(value)}</span>
        <span className="font-num text-xs text-muted-foreground w-24 text-right hidden md:inline">{cmpKey && cmp[cmpKey]?.abs != null ? `Δ ${formatBRL(cmp[cmpKey].abs)}` : ""}</span>
      </span>
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">DRE Gerencial Simplificada</h1>
          <p className="text-muted-foreground mt-1">Faturamento não é lucro — clique nas linhas para rastrear a origem</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportPdf} data-testid="dre-export-pdf"><FileText className="h-4 w-4 mr-1" /> Exportar PDF</Button>
          <Button variant="outline" onClick={exportXlsx} data-testid="dre-export-xlsx"><Sheet className="h-4 w-4 mr-1" /> Exportar Excel</Button>
        </div>
      </div>

      {loading || !c ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      ) : (
        <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden max-w-3xl" data-testid="dre-table">
          <div className="flex items-center justify-end gap-6 px-5 py-2 text-xs text-muted-foreground border-b border-border">
            <span className="w-28 text-right">R$</span><span className="w-16 text-right hidden sm:inline">% da RB</span><span className="w-24 text-right hidden md:inline">vs anterior</span>
          </div>
          <Row label="Receita Bruta" value={c.receita_bruta} onClick={openSales} testid="dre-receita-bruta" />
          <Row label="Deduções comerciais" value={c.deducoes} sign="(-) " testid="dre-deducoes" />
          <Row label="Receita Líquida" value={c.receita_liquida} strong cmpKey="receita_liquida" testid="dre-receita-liquida" />
          <Row label="CMV — Custo da Mercadoria Vendida" value={c.cmv} sign="(-) " onClick={openCmv} cmpKey="cmv" testid="dre-cmv" />
          <Row label="Margem Bruta" value={c.margem_bruta} strong cmpKey="margem_bruta" testid="dre-margem-bruta" />
          <Row label="Despesas Variáveis" value={c.despesas_variaveis} sign="(-) " onClick={() => openExpenses("VARIABLE")} cmpKey="despesas_variaveis" testid="dre-desp-variaveis" />
          <Row label="Margem de Contribuição" value={c.margem_contribuicao} strong cmpKey="margem_contribuicao" testid="dre-margem-contribuicao" />
          <Row label="Despesas Fixas" value={c.despesas_fixas} sign="(-) " onClick={() => openExpenses("FIXED")} cmpKey="despesas_fixas" testid="dre-desp-fixas" />
          <Row label="Resultado Operacional Gerencial" value={c.resultado_operacional} strong isResult cmpKey="resultado_operacional" testid="dre-resultado" />
        </div>
      )}

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{drill?.title}</DialogTitle>
            <DialogDescription>
              {drill?.type === "sales"
                ? "Vendas confirmadas que compõem a receita do período."
                : drill?.type === "cmv"
                ? "Cada mercadoria vendida no período, com custo unitário do lote (rateio) × quantidade = CMV."
                : "Composição por categoria — clique numa categoria para ver os lançamentos que a formam. Valores cancelados não entram no total."}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {drill?.type === "sales" && (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2">Venda</th><th className="py-2">Data</th><th className="py-2 text-right">Líquido</th><th className="py-2 text-right">CMV</th></tr></thead>
                <tbody>
                  {drill.rows.map((s) => (
                    <tr key={s.id} className="border-b border-border last:border-0">
                      <td className="py-2 font-num">{s.sale_number}</td>
                      <td className="py-2">{formatDateTime(s.timestamp)}</td>
                      <td className="py-2 text-right font-num">{formatBRL(s.total_net)}</td>
                      <td className="py-2 text-right font-num">{formatBRL((s.lines || []).reduce((a, l) => a + (l.cost_total || 0), 0))}</td>
                    </tr>
                  ))}
                  {drill.rows.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Sem vendas no período</td></tr>}
                </tbody>
              </table>
            )}
            {drill?.type === "cmv" && (
              <table className="w-full text-sm" data-testid="dre-cmv-drill">
                <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2">Venda</th><th className="py-2">Mercadoria</th><th className="py-2">Lote</th><th className="py-2 text-center">Qtd</th><th className="py-2 text-right">Custo unit.</th><th className="py-2 text-right">Custo total</th></tr></thead>
                <tbody>
                  {drill.rows.map((r, idx) => (
                    <tr key={`${r.sale_number}-${r.item_code}-${idx}`} className="border-b border-border last:border-0" data-testid={`dre-cmv-item-${idx}`}>
                      <td className="py-2 font-num">{r.sale_number || "—"}</td>
                      <td className="py-2"><span className="font-num text-xs text-muted-foreground">{r.item_code}</span> {r.item_description}
                        {r.frozen && <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide rounded bg-sky-100 text-sky-700 px-1.5 py-0.5" title="Pertence a uma competência fechada/bloqueada — custo congelado"><Snowflake className="h-3 w-3" />Congelado</span>}
                      </td>
                      <td className="py-2 font-num text-xs text-muted-foreground">{r.lot_code || "—"}</td>
                      <td className="py-2 text-center font-num">{r.quantity}</td>
                      <td className="py-2 text-right font-num">{formatBRL(r.unit_cost)}</td>
                      <td className="py-2 text-right font-num">{formatBRL(r.cost_total)}</td>
                    </tr>
                  ))}
                  {drill.rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sem mercadorias vendidas no período</td></tr>}
                </tbody>
                {drill.rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-border font-semibold">
                      <td className="py-2" colSpan={5}>Total do CMV</td>
                      <td className="py-2 text-right font-num" data-testid="dre-cmv-drill-total">{formatBRL(drill.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
            {drill?.type === "expenses" && (
              <table className="w-full text-sm" data-testid="dre-expenses-drill">
                <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2">Categoria</th><th className="py-2 text-center">Lançamentos</th><th className="py-2 text-right">Valor</th></tr></thead>
                <tbody>
                  {drill.rows.map((e) => {
                    const isOpen = !!expanded[e.name];
                    return (
                      <React.Fragment key={e.name}>
                        <tr
                          className="border-b border-border cursor-pointer hover:bg-secondary/60"
                          onClick={() => setExpanded((p) => ({ ...p, [e.name]: !p[e.name] }))}
                          data-testid={`dre-expense-cat-${e.name}`}
                        >
                          <td className="py-2">
                            <span className="inline-flex items-center gap-1.5">
                              {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                              {e.name}
                              {e.auto && <span className="text-[10px] uppercase tracking-wide rounded bg-accent/10 text-accent px-1.5 py-0.5">regra automática</span>}
                            </span>
                          </td>
                          <td className="py-2 text-center text-muted-foreground font-num">{e.count}</td>
                          <td className="py-2 text-right font-num">{formatBRL(e.amount)}</td>
                        </tr>
                        {isOpen && e.items.map((it, idx) => (
                          <tr key={it.expense_id || it.rule_id || idx} className="border-b border-border bg-secondary/30 text-xs" data-testid={`dre-expense-item-${it.expense_id || it.rule_id || idx}`}>
                            <td className="py-1.5 pl-8 text-muted-foreground">
                              {it.description || (it.auto ? "Regra automática" : "Sem descrição")}
                              <span className="block text-[11px] text-muted-foreground/70">
                                {it.competence_date ? `Competência ${formatDate(it.competence_date)}` : "Calculado sobre o período"}
                              </span>
                            </td>
                            <td></td>
                            <td className="py-1.5 text-right font-num">{formatBRL(it.amount)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {drill.rows.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-muted-foreground">Sem lançamentos no período</td></tr>}
                </tbody>
                {drill.rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-border font-semibold">
                      <td className="py-2">Total</td>
                      <td className="py-2 text-center text-muted-foreground font-num">{drill.rows.reduce((a, r) => a + r.count, 0)}</td>
                      <td className="py-2 text-right font-num" data-testid="dre-expenses-drill-total">{formatBRL(drill.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
